import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { markBillPaid } from "@/lib/api";

const PAYMENT_MODES = ["Cash", "UPI", "Cheque", "Bank Transfer", "Card"];

export default function PaymentDialog({ bill, open, onOpenChange, onPaid }) {
  const [form, setForm] = useState({
    amountReceived: 0,
    paymentMode: "Cash",
    referenceNo: "",
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  useEffect(() => {
    if (bill && open) {
      const previouslyPaid = bill.totalPaid || (bill.isPaid ? (bill.netPayable || 0) : 0);
      const remaining = Math.max(0, (bill.netPayable || 0) - previouslyPaid);
      setForm({
        amountReceived: remaining,
        paymentMode: "Cash",
        referenceNo: "",
        paymentDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
    }
  }, [bill, open]);

  const handleSubmit = async () => {
    if (!form.amountReceived || form.amountReceived <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const updated = await markBillPaid(bill.id, form);
    if (updated) {
      const status = updated.status || (updated.isPaid ? "paid" : "unpaid");
      const msg = status === "paid" ? "Payment recorded — Bill fully paid!" :
                  status === "partial" ? "Partial payment recorded." : "Payment recorded.";
      toast.success(msg, {
        description: `Receipt: ${updated.paymentDetails?.receiptNo || ""}`,
      });
      onPaid?.(updated);
      onOpenChange(false);
    }
  };

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  if (!bill) return null;

  const previouslyPaid = bill.totalPaid || (bill.isPaid ? (bill.netPayable || 0) : 0);
  const remaining = Math.max(0, (bill.netPayable || 0) - previouslyPaid);
  const afterPayment = previouslyPaid + (form.amountReceived || 0);
  const willBeStatus = afterPayment >= (bill.netPayable || 0) ? "paid" : afterPayment > 0 ? "partial" : "unpaid";
  const isOverpayment = form.amountReceived > remaining;
  const advanceAmount = Math.max(0, afterPayment - (bill.netPayable || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold">Record Payment</DialogTitle>
          <DialogDescription>
            {bill.customerName} — Invoice {bill.invoiceNo}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 bg-zinc-50 border border-zinc-200 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-zinc-500">Bill Amount</span>
              <span className="font-mono font-semibold">{"\u20B9"}{(bill.netPayable || 0).toFixed(2)}</span>
            </div>
            {previouslyPaid > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Previously Paid</span>
                <span className="font-mono text-emerald-600">{"\u20B9"}{previouslyPaid.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span className="text-zinc-500">Remaining</span>
              <span className="font-mono text-red-600">{"\u20B9"}{remaining.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <Label>Amount Received *</Label>
            <Input
              data-testid="payment-amount-input"
              type="number"
              step="0.01"
              value={form.amountReceived}
              onChange={(e) => update("amountReceived", parseFloat(e.target.value) || 0)}
              className="bg-zinc-50 border-zinc-300 rounded-none mt-1"
            />
            {/* Status preview */}
            <div className="mt-2 flex items-center gap-2">
              {willBeStatus === "paid" && !isOverpayment && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Will be: Fully Paid</Badge>}
              {willBeStatus === "paid" && isOverpayment && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Advance: {"\u20B9"}{advanceAmount.toFixed(2)} credited</Badge>
              )}
              {willBeStatus === "partial" && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Partial — {"\u20B9"}{((bill.netPayable || 0) - afterPayment).toFixed(2)} will remain</Badge>}
            </div>
          </div>

          <div>
            <Label>Mode of Payment *</Label>
            <Select value={form.paymentMode} onValueChange={(v) => update("paymentMode", v)}>
              <SelectTrigger data-testid="payment-mode-select" className="bg-zinc-50 border-zinc-300 rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Reference No / Transaction ID</Label>
            <Input data-testid="payment-reference-input" value={form.referenceNo} onChange={(e) => update("referenceNo", e.target.value)} placeholder="e.g. TXN12345" className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>

          <div>
            <Label>Payment Date</Label>
            <Input data-testid="payment-date-input" type="date" value={form.paymentDate} onChange={(e) => update("paymentDate", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>

          <div>
            <Label>Notes</Label>
            <Input data-testid="payment-notes-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Optional remarks" className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
          </div>
        </div>

        <Separator />

        <DialogFooter>
          <Button data-testid="record-payment-button" onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
